import * as React from 'react';
import styled from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { Event } from '@process-advisor/process-miner/api/events';
import {
    getColor,
    getFontFamily,
    getLetterSpacing,
    getLineHeight,
} from '@process-advisor/process-miner/shared/theme';

const Grid = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    padding-top: 5px;
    padding-left: 25px;
    grid-template-columns: 100px 100px 100px 100px 100px;
`;

const MetricContainer1 = styled.div`
    flex-direction: column;
    align-items: center;
    position: relative;
    display: flex;
    height: 100%
    grid-row: 1 ;
    grid-column: 1 ;
`;
const MetricContainer2 = styled.div`
    flex-direction: column;
    align-items: center;
    position: relative;
    display: flex;
    height: 100%
    grid-row: 1 ;
    grid-column: 2 ;
`;

const MetricContainer3 = styled.div`
    flex-direction: column;
    align-items: center;
    position: relative;
    display: flex;
    height: 100%
    grid-row: 1 ;
    grid-column: 3 ;
`;

const MetricContainer4 = styled.div`
    flex-direction: column;
    align-items: center;
    position: relative;
    display: flex;
    height: 100%
    grid-row: 1 ;
    grid-column: 4 ;
`;

const MetricContainer5 = styled.div`
    flex-direction: column;
    align-items: center;
    position: relative;
    display: flex;
    height: 100%
    grid-row: 1 ;
    grid-column: 5 ;
`;

//     color: ${getColor('blue')};
const Gauge = styled(CircularProgress)<{ gaugecolor?: string }>`
    color: ${({ gaugecolor }) => gaugecolor};   
    &.MuiCircularProgress-root .MuiCircularProgress-determinate {
        height: 60px;
        width: 60px;
    }
`;

export const Label = styled.div`
    text-align: center;
    color: ${getColor('darkFontColor')};
    font-family: ${getFontFamily('atlassian')};
    font-size: 12px;
    letter-spacing: ${getLetterSpacing('normal')};
    line-height: ${getLineHeight('smaller')};
    padding-top: 5px;
`;


export const LabelValue = styled.div`
    text-align: center;
    color: ${getColor('blue')};
    font-family: ${getFontFamily('atlassian')};
    font-size: 16px;
    font-weight: bold;
    letter-spacing: ${getLetterSpacing('normal')};
    line-height: ${getLineHeight('smaller')};
    padding-top: 22px;
    padding-bottom: 17px;
`;
const GaugeBackground = styled(CircularProgress)<{ gaugebackgroundcolor?: string }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) !important;
    color: ${({ gaugebackgroundcolor }) => gaugebackgroundcolor};  
`;


type GaugesProps = {
    activeComponent?: (value: string) => void;
};

export const ProcessMinerGauges: React.FC<GaugesProps> = ({ activeComponent }) => {
    const [eventsSelected, setEventsSelected] = React.useState<number>(100);
    const [eventsTotal, setTotalEvents] = React.useState<number>(200);
    const [tasksSelected, setTasksSelected] = React.useState<number>(100);
    const [tasksTotal, setTotalTasks] = React.useState<number>(200);
    const [variantsSelected, setVariantsSelected] = React.useState<number>(100);
    const [variantsTotal, setTotalVariants] = React.useState<number>(200);
    const [stepsTotal, setTotalSteps] = React.useState<number>(200);
    const [stepsSelected, setStepsSelected] = React.useState<number>(200);
    let [activitiesTotal, setTotalActivities] = React.useState<number>(200);
    let [activitiesSelected, setActivitiesSelected] = React.useState<number>(200);

    React.useEffect(() => {

        const updateSelectedVariantListener = () => {
            let stats = apiProvider.processMinerGauges()
            setEventsSelected(stats[0].events);
            setTotalEvents(stats[0].events_total);
            setTasksSelected(stats[0].tasks);
            setTotalTasks(stats[0].tasks_total);
            setVariantsSelected(stats[0].variants);
            setTotalVariants(stats[0].variants_total);
            setStepsSelected(stats[0].steps);
            setTotalSteps(stats[0].steps_total);
            setActivitiesSelected(stats[0].activities);
            setTotalActivities(stats[0].activities_total);
        };

        apiProvider.on(
            Event.updateGaugeMetrics,
            updateSelectedVariantListener
        );

        apiProvider.updateGauges({});

        return () => {
            apiProvider.off(
                Event.updateGaugeMetrics,
                updateSelectedVariantListener
            );
        };
    }, []);

    // The runtime can briefly report negative/undefined intermediate values while
    // rebuilding a model. Never surface those implementation details in the UI.
    // Keep React state immutable and derive safe display values instead of
    // reassigning the const values returned by useState.
    const safeVariantsSelected = Math.max(0, Number(variantsSelected) || 0);
    const safeVariantsTotal = Math.max(0, Number(variantsTotal) || 0);
    const safeStepsSelected = Math.max(0, Number(stepsSelected) || 0);
    const safeStepsTotal = Math.max(0, Number(stepsTotal) || 0);
    const safeActivitiesSelected = Math.max(0, Number(activitiesSelected) || 0);
    const safeActivitiesTotal = Math.max(0, Number(activitiesTotal) || 0);
    const safeTasksSelected = Math.max(0, Number(tasksSelected) || 0);
    const safeTasksTotal = Math.max(0, Number(tasksTotal) || 0);
    const safeEventsSelected = Math.max(0, Number(eventsSelected) || 0);
    const safeEventsTotal = Math.max(0, Number(eventsTotal) || 0);

    let percVariants = safeVariantsTotal > 0 ? (safeVariantsSelected / safeVariantsTotal) * 100 : 0;
    let percTasks = safeTasksTotal > 0 ? (safeTasksSelected / safeTasksTotal) * 100 : 0;
    let percEvents = safeEventsTotal > 0 ? (safeEventsSelected / safeEventsTotal) * 100 : 0;

    const variantsLabel = 'of ' + apiProvider.formatNumber({ count: safeVariantsTotal }) + ' variants';
    const eventsLabel = 'of ' + apiProvider.formatNumber({ count: safeEventsTotal }) + ' events';
    const tasksLabel = 'of ' + apiProvider.formatNumber({ count: safeTasksTotal }) + ' ' + apiProvider.getEntityLower() + 's'
    const stepsLabel = 'of ' + safeStepsTotal + ' steps';
    const activitiesLabel = 'of ' + safeActivitiesTotal + ' activities';
    const defaultGaugeColor = '#0B66E4'; // blue
    const defaultGaugeBackgroundColor = '#9E9E9E'; // gray

    return (
        <div id="#gauges">
        <Grid>
            <MetricContainer1>
                <Box position="relative" display="inline-flex">
                    <GaugeBackground
                        size={50}
                        value={100}
                        gaugebackgroundcolor={defaultGaugeBackgroundColor}
                        variant="determinate" />
                    <Gauge
                        size={50}
                        variant="determinate"
                        gaugecolor={defaultGaugeColor}
                        value={percVariants} />
                    <Box
                        top={0}
                        left={0}
                        bottom={0}
                        right={0}
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="caption"
                            component="div"
                            fontSize={"12px"}
                            color="textSecondary">
                            {`${Math.round(safeVariantsSelected)}`}
                        </Typography>
                    </Box>
                </Box>
                <Label>{variantsLabel}</Label>
            </MetricContainer1>
            <MetricContainer2>
                <Box position="relative" display="inline-flex">
                    <GaugeBackground
                        size={50}
                        value={100}
                        gaugebackgroundcolor={defaultGaugeBackgroundColor}
                        variant="determinate" />
                    <Gauge
                        size={50}
                        variant="determinate"
                        gaugecolor={defaultGaugeColor}
                        value={percTasks} />
                    <Box
                        top={0}
                        left={0}
                        bottom={0}
                        right={0}
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="caption"
                            component="div"
                            fontSize={"12px"}
                            color="textSecondary">
                            {`${Math.round(safeStepsSelected)}`}
                        </Typography>
                    </Box>
                </Box>
                <Label>{stepsLabel}</Label>
            </MetricContainer2>

            <MetricContainer3>
                <Box position="relative" display="inline-flex">
                    <GaugeBackground
                        size={50}
                        value={100}
                        gaugebackgroundcolor={defaultGaugeBackgroundColor}
                        variant="determinate" />
                    <Gauge
                        size={50}
                        variant="determinate"
                        gaugecolor={defaultGaugeColor}
                        value={percTasks} />
                    <Box
                        top={0}
                        left={0}
                        bottom={0}
                        right={0}
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="caption"
                            component="div"
                            fontSize={"12px"}
                            color="textSecondary">
                            {`${Math.round(safeActivitiesSelected)}`}
                        </Typography>
                    </Box>
                </Box>
                <Label>{activitiesLabel}</Label>
            </MetricContainer3>
            <MetricContainer4>
                <Box position="relative" display="inline-flex">
                    <GaugeBackground
                        size={50}
                        value={100}
                        gaugebackgroundcolor={defaultGaugeBackgroundColor}
                        variant="determinate" />
                    <Gauge
                        size={50}
                        variant="determinate"
                        gaugecolor={defaultGaugeColor}
                        value={percTasks} />
                    <Box
                        top={0}
                        left={0}
                        bottom={0}
                        right={0}
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="caption"
                            component="div"
                            fontSize={"12px"}
                            color="textSecondary">
                            {`${Math.round(percTasks,)}%`}
                        </Typography>
                    </Box>
                </Box>
                <Label>{tasksLabel}</Label>
            </MetricContainer4>
            <MetricContainer5>
                <Box position="relative" display="inline-flex">
                    <GaugeBackground
                        size={50}
                        value={100}
                        gaugebackgroundcolor={defaultGaugeBackgroundColor}
                        variant="determinate" />
                    <Gauge
                        size={50}
                        gaugecolor={defaultGaugeColor}
                        variant="determinate"
                        value={percEvents}/>
                    <Box
                        top={0}
                        left={0}
                        bottom={0}
                        right={0}
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="caption"
                            component="div"
                            fontSize={"12px"}
                            color="textSecondary">
                            {`${Math.round(percEvents,)}%`}
                        </Typography>
                    </Box>
                </Box>
                <Label>{eventsLabel}</Label>
            </MetricContainer5>            
        </Grid>
        </div>
    );
}
